<svelte:options tag={null}/>
<script lang="typescript">
  import type {
    PlayerInfo
  } from './PlayerInfo.types';
  import "@everymatrix/em-card/dist/em-card";
  import "@everymatrix/retail-spinner/dist/retail-spinner";
  import { getData, getHeaders, isPermissionPresent } from '@everymatrix/retail-helpers';

	export let src: string = null;
  export let playerid: string = null;
  export let isexpandable: string = 'false';
  export let expandicon: string = 'false';
  export let displayfields: string = 'true';
  let openContent: boolean = false;

  let edit: boolean = false;
  let playerInfo: PlayerInfo;
  let playerForm: PlayerInfo;

  let errorMessage: string = null;
  let isLoading: boolean = false;
  let success: boolean = false;

  let allBlockTypes: string[];
  let permissions;

  const statusesList = ['Active', 'Blocked'];
  permissions = {};

  const fetchPlayerDetails = async (src: string) => {
    const {data, error} = await getData(src, {requiresHeaders: true});
    if(data) {
      return {
        id: data.id,
        userName: data.userName,
        firstName: data.firstName,
        surName: data.lastName,
        birthDate: formatDate(data.birthDate),
        email: data.email,
        mobilePhonePrefix: data.mobilePhonePrefix,
        mobilePhone: data.mobilePhone,
        postCode: data.zip,
        countryId: data.countryId,
        stateId: data.stateId,
        language: data.language,
        status: data.activeStatus,
        gender: data.gender,
        lastLogin: formatDate(data.lastLogin),
        registrationTime: formatDate(data.registrationTime)
      }
    }
    if(error) throw new Error(error.message);
  }

  const fetchAllBlockTypes = async (src: string) => {
    const {data, error} = await getData(src, {requiresHeaders: true});
    if(data) return data;
    if(error) throw new Error(error.message);
  }

  const fetchPlayerData = async (src: string, playerid: string) => {
    try{
      isLoading = true;
      const [playerDetails, blockTypes] = await Promise.all(
        [
          fetchPlayerDetails(`${src}${playerid}/details`),
          fetchAllBlockTypes(`${src}blocktypes`).catch(e => console.log(e))
        ]
      )
      playerInfo = {...playerDetails}
      if(blockTypes) allBlockTypes = [...blockTypes];
      // Fast deepclone 
      playerForm = JSON.parse(JSON.stringify(playerInfo));
      
    }catch(err) {
      errorMessage = err.message;
    }finally {
      isLoading = false;
    }
  }

  const formatDate = (date: string) => {
    if(date){
      return date.split('T')[0];
    }
    return date;
  };
  
  const updateDetails = async (src: string, playerid: string, playerForm: PlayerInfo, headers: HeadersInit) => {
    try {
      isLoading = true;
      const res = await fetch(`${src}${playerid}/details`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(playerForm),
      })
      const data = await res.json();
      if(res.ok){ 
        return data;
      }else{
        throw new Error(data.message);
      }
    }catch(error){
      throw new Error(error.message);
    }finally{
      isLoading = false;
    }
  }

  const changePlayerStatus = async (src: string, playerid: string, newStatus: string, blockReason: string = 'Unknown', headers: HeadersInit) => {
    try {
      let url = `${src}${playerid}/activestatus?NewStatus=${newStatus}`;
      if(blockReason && newStatus === 'Blocked') url += `&BlockReason=${blockReason}`;
      const res = await fetch(url, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (res.ok) {
        return data;
      }
    } catch (error) {
      throw new Error(error.message);
    }
  };

  const handleOnCancel = () => {
    edit = false;
    playerForm = {...playerInfo};
  }

  const handleOnSave = async () => {  
    try {
      isLoading = true;
      errorMessage = null;
      const headers = await getHeaders();
      const [details, status] = await Promise.all([
        updateDetails(src, playerid, playerForm, headers),
        playerInfo.status !== playerForm.status && 
          changePlayerStatus(src, playerid, playerForm.status, playerForm.blockReason, headers),
      ]);
      if(details || status) {
        await fetchPlayerData(src, playerid);
        success = true;
      }
    } catch (error) {
      errorMessage = 'Cannot update player details';
    }finally{
      isLoading = false;
      edit = false;
    }
  }

  const getPemissions = async () => {
    [ permissions.canEditPlayerInfo,
      permissions.canReadFirstName,
      permissions.canEditFirstName, 
      permissions.canReadLastName, 
      permissions.canEditLastName, 
      permissions.canReadDOB, 
      permissions.canEditDOB, 
      permissions.canReadGender, 
      permissions.canReadEmail, 
      permissions.canEditEmail, 
      permissions.canReadLastLogin, 
      permissions.canReadRegistration,
      permissions.canReadStatus,
      permissions.canEditStatus] = await Promise.all([
        isPermissionPresent(src, 'upp.dashboard.player-info', 'WRITE'), 
        isPermissionPresent(src, 'upp.dashboard.player-info.first-name', 'READ'), 
        isPermissionPresent(src, 'upp.dashboard.player-info.first-name', 'WRITE'), 
        isPermissionPresent(src, 'upp.dashboard.player-info.last-name', 'READ'),
        isPermissionPresent(src, 'upp.dashboard.player-info.last-name', 'WRITE'), 
        isPermissionPresent(src, 'upp.dashboard.player-info.dob', 'READ'),
        isPermissionPresent(src, 'upp.dashboard.player-info.dob', 'WRITE'), 
        isPermissionPresent(src, 'upp.dashboard.player-info.gender', 'READ'),
        isPermissionPresent(src, 'upp.dashboard.player-info.email', 'READ'), 
        isPermissionPresent(src, 'upp.dashboard.player-info.email', 'WRITE'),
        isPermissionPresent(src, 'upp.dashboard.player-info.last-login', 'READ'), 
        isPermissionPresent(src, 'upp.dashboard.player-info.registration', 'READ'),
        isPermissionPresent(src, 'upp.dashboard.player-info.status', 'READ'),
        isPermissionPresent(src, 'upp.dashboard.player-info.status', 'WRITE')
      ]);
  }

  $: src && playerid && fetchPlayerData(src, playerid)
  $: src && getPemissions();
</script>


<em-card>
  <!-- Header -->
  <div 
    slot="header" 
    class="row header" 
    on:click={() => { 
      (isexpandable === 'true') ? openContent = !openContent : null 
    }}
  >
    <div class="title">
      <h3>Player Info</h3>
    {#if expandicon === 'true'}
      <svg
        class="header_icon {openContent ? 'active' : ''}"
        fill="currentColor" 
        viewBox="0 0 20 20" 
        xmlns="http://www.w3.org/2000/svg"
      >
        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"></path>
      </svg>
    {/if}
    </div>
    
    <div class="actions">
      {#if !edit}
        {#if permissions.canEditPlayerInfo}
        <div class="icon__wrapper" on:click|stopPropagation={() => {edit = true}}>
          <svg class="icon" fill="#2344b0" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path></svg>
        </div>
        {/if}
      {:else}
      <div class="icon__wrapper" on:click|stopPropagation={handleOnCancel}>
        <svg class="icon" fill="#cc2127" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
      </div>
      <div class="icon__wrapper" on:click|stopPropagation={handleOnSave}>
        <svg class="icon" fill="#23b04e" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>
      </div>
      {/if}
    </div>
  </div>

  <!-- Content -->
  <div slot="content">
    {#if isLoading}
      <ul class="loader">
        <li></li>
        <li></li>
        <li></li>
        <li></li>
        <li></li>
        <li></li>
        <li></li>
        <li></li>
        <li></li>
      </ul>
    {:else if playerInfo}
      {#if !edit}
      <ul class='info'>
        {#if isexpandable === 'true' && !openContent}
          <li class='info__item'>
            <strong>Username: </strong>{playerInfo.userName}
          </li>
        {:else}
          <li class='info__item'>
            <strong>Id: </strong>{playerInfo.id}
          </li>
          <li class='info__item'>
            <strong>Username: </strong>
            {playerInfo.userName}
          </li>
          {#if permissions.canReadFirstName}
            <li class='info__item'>
              <strong>First Name: </strong>{playerInfo.firstName}
            </li>
          {/if}
          {#if permissions.canReadLastName}
          <li class='info__item has-break'>
            <strong>Last Name: </strong>{playerInfo.surName}
          </li>
          {/if}
          {#if permissions.canReadDOB}
          <li class='info__item has-break'>
            <strong>Date of Birth: </strong>{playerInfo.birthDate}
          </li>
          {/if}
          {#if displayfields === "true" && permissions.canReadGender}
            <li class='info__item has-break'>
              <strong>Gender: </strong>{playerInfo.gender}
            </li>
          {/if}
          {#if permissions.canReadEmail}
          <li class='info__item'>
            <strong>Email: </strong>{playerInfo.email}
          </li>
          {/if}
          {#if permissions.canReadLastLogin}
          <li class='info__item'>
            <strong>Last Login: </strong>{playerInfo.lastLogin}
          </li>
          {/if}
          {#if displayfields === "true" && permissions.canReadStatus}
            <li class='info__item'>
              <strong>Status: </strong>{playerInfo.status}
            </li>
          {/if}
          {#if permissions.canReadRegistration}        
          <li class='info__item'>
            <strong>Registration Date: </strong>{playerInfo.registrationTime ?? 'N/A'}
          </li>
          {/if}
        {/if}
      </ul>
      {:else}
        <form class="userForm">
          <fieldset class="userForm__fieldset">
            <label class="userForm__field" for="userid">
              <span>Id</span>
              <input name="userid" disabled type="text" bind:value={playerForm.id}>
            </label>
            
            <label class="userForm__field" for="username">
              <span>Username</span>
              <input name="username" disabled type="text" bind:value={playerForm.userName}>
            </label>

            {#if permissions.canReadFirstName}
              <label class="userForm__field" for="firstname">
                <span>First Name</span>
                <input name="firstname" type="text" disabled={!permissions.canEditFirstName} bind:value={playerForm.firstName}>
              </label>
            {/if}

            {#if permissions.canReadLastName}
              <label class="userForm__field" for="lastname">
                <span>Last Name</span>
                <input name="lastname" type="text"disabled={!permissions.canEditLastName} bind:value={playerForm.surName}>
              </label>
            {/if}
            
            {#if permissions.canReadDOB}
              <label class="userForm__field" for="date">
                <span>Date of Birth</span>
                <input name="date" type="date" disabled={!permissions.canEditDOB} bind:value={playerForm.birthDate}>
              </label>
            {/if}
            {#if displayfields === "true" && permissions.canReadGender}
              <label class="userForm__field" for="gender">
                <span>Gender</span>
                <input name="gender" disabled type="text" bind:value={playerForm.gender}>
              </label>
            {/if}
          </fieldset>

          <fieldset class="userForm__fieldset">
            {#if permissions.canReadEmail}
              <label class="userForm__field" for="email">
                <span>Email </span>
                <input name="email" type="email" disabled={!permissions.canEditEmail} bind:value={playerForm.email}>
              </label>
            {/if}

            {#if permissions.canReadLastLogin}
              <label class="userForm__field" for="lastlogin">
                <span>Last Login </span>
                <input name="lastlogin" disabled type="text" bind:value={playerForm.lastLogin}>
              </label>
            {/if}

            {#if permissions.canReadRegistration}
              <label class="userForm__field" for="lastlogin">
                <span>Registration Date </span>
                <input name="registrationDate" disabled type="text" bind:value={playerForm.registrationTime}>
              </label>
            {/if}
          </fieldset>

          <fieldset class="userForm__fieldset">                
            {#if displayfields === "true" && permissions.canReadStatus}       
              <label class="userForm__field" for="status">
                <span>Status</span>
                <select name="status" disabled={!permissions.canEditStatus} bind:value={playerForm.status}>
                  {#each statusesList as status}
                    <option value={status}>{status}</option>
                  {/each}
                </select>
              </label>            
            
              {#if playerForm.status === 'Blocked'}
                {#if allBlockTypes}
                  <label class="userForm__field" for="reason">
                    <span>Reason</span>
                    <select name="reason" bind:value={playerForm.blockReason}>
                      {#each allBlockTypes as reason}
                        <option value={reason}>{reason}</option>
                      {/each}
                    </select>
                  </label>
                {/if}
              {/if}
            {/if}
          </fieldset>
        </form>
      {/if}
    {/if}
    {#if errorMessage}
      <p style="color: red">{errorMessage}</p>
    {/if}
    {#if success}
      <p style="color: green">Player Details updated</p>
    {/if}
  </div>
</em-card>
<!-- <select bind:value={playerForm.mobilePhonePrefix}>
  {#if playerForm.mobilePhonePrefix !== allPhoneCodes.filter(code => code.CountryID === playerForm.countryId)[0].Prefix}
    <option value={playerForm.mobilePhonePrefix}>{playerForm.mobilePhonePrefix}</option>
  {/if}
  {#each allPhoneCodes.filter(code => code.CountryID === playerForm.countryId) as phoneCode}
    <option value={phoneCode.Prefix}>{phoneCode.Prefix}</option>
  {/each}
</select> -->
<style lang="scss">
  *,
  *::before,
  *::after {
    margin: 0;
    padding: 0;
    list-style: none;
    outline: none;
    text-decoration: none;
    box-sizing: border-box;
    font-family: "Helvetica Neue", "Helvetica", sans-serif;
  }
  
  
  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .header {
    justify-content: space-between;
    align-items: center;
  }
  .title{
    display: flex;
    flex-direction: row;
  }
  .actions {
    display: flex;
    flex-direction: row;
  }
  .icon {
    width: 20px;
    height: 20px;
    &__wrapper {
      margin-right: 20px;
      &:last-child { margin-right: 0; }
    }
  }

  .info {
    display: grid;
    grid-template-columns: 1fr;
    row-gap: 5px;
  }

  .userForm {

    &__fieldset,
    &__field {
      display: flex;
      flex-direction: column;
    }

    &__fieldset {
      margin-bottom: 10px;
      border: none;
    }

    &__field {
      margin-bottom: 5px;

      span { 
        margin-bottom: 5px; 
        font-weight: 600;
      }
      select {
        display: flex;
        box-sizing: border-box;
        width: 100%;
      }
    }

  }
  
  input:focus{
    border: 1px solid #23b04e;

  }

  .loader {
    min-width: 200px;

    li {
      position: relative;
      height: 18px;
      margin-bottom: 10px;
      animation-duration: 1.25s;
      animation-fill-mode: forwards;
      animation-iteration-count: infinite;
      animation-name: shimmer;
      animation-timing-function: linear;
      background: #F6F6F6;
      background: linear-gradient(to right, #F6F6F6 8%, #F0F0F0 18%, #f5f5f5 33%);
      background-size: 800px 104px;
      &:last-child { margin-bottom: 0; }
    }
  }

  .header_icon { 
    width: 20px; 
    height: 20px; 
  }
  .header_icon.active { transform: rotate(180deg); }
  // Animation
  @keyframes shimmer{
    0%{
      background-position: -468px 0
    }
    100%{
      background-position: 468px 0
    }
  }
</style>
